import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as MiniCalendar } from "@/components/ui/calendar";
import { da } from "date-fns/locale";
import { toast } from "sonner";
import { 
  ArrowLeft, Calendar as CalendarIcon, ChevronLeft, ChevronRight, 
  Package, Clock, AlertTriangle, Bell, Plus, X, Check, Settings, Trash2, Copy
} from "lucide-react";

// Hjælpefunktioner til datoer
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
// Mandag-indekseret: returnerer 0 for mandag, 6 for søndag (dansk konvention)
const getFirstDayOfMonth = (year, month) => {
  const sundayBased = new Date(year, month, 1).getDay();  // 0=Sun, 1=Mon, ..., 6=Sat
  return (sundayBased + 6) % 7;  // → 0=Mon, ..., 6=Sun
};
const formatDate = (date) => date.toISOString().split('T')[0];

// ISO-ugenummer (uge der indeholder torsdag = den uges nummer)
const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Find mandag i samme uge som dato (uger starter mandag i DK)
const getMondayOfWeek = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNum = d.getDay() || 7; // 1=Mon...7=Sun
  d.setDate(d.getDate() - (dayNum - 1));
  return d;
};

// Returnér dags dato ved midnat (lokal tid) uden at mutere en eksisterende Date
const todayMidnight = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
};

// Materiel-deadlines har faste, hardcodede farver (de er ikke brugerredigerbare)
const MATERIEL_TYPE_COLORS = {
  materiel_bestilling: '#3b82f6', // blue
  materiel_endelig:    '#f97316', // orange
};

// Konvertér hex farve til RGBA med alpha (til baggrundsfarver med transparens)
const hexToRgba = (hex, alpha = 0.2) => {
  if (!hex || !hex.startsWith('#')) return `rgba(168,85,247,${alpha})`;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// 12-farvers kontrast-palette til farvevælger
const COLOR_PALETTE = [
  '#3b82f6', // blue
  '#f97316', // orange
  '#22c55e', // green
  '#8b5cf6', // purple
  '#eab308', // yellow
  '#06b6d4', // cyan
  '#ef4444', // red
  '#ec4899', // pink
  '#10b981', // emerald
  '#a855f7', // violet
  '#0ea5e9', // sky
  '#f59e0b', // amber
];

// ---- Spændende deadlines (kan strække sig over minutter/timer/dage) ----
// Slutdato falder tilbage til startdato hvis ikke sat.
const deadlineEndDate = (d) => d.end_date || d.date;

// Dækker denne deadline en given dato (YYYY-MM-DD)? Streng-sammenligning virker for ISO-datoer.
const deadlineCoversDate = (d, dateStr) => dateStr >= d.date && dateStr <= deadlineEndDate(d);

// Info om hvor en given dato ligger i deadline-spændet
const getSpanInfo = (d, dateStr) => {
  const endDate = deadlineEndDate(d);
  const isMultiDay = endDate !== d.date;
  const isStart = dateStr === d.date;
  const isEnd = dateStr === endDate;
  return { isMultiDay, isStart, isEnd, isMiddle: isMultiDay && !isStart && !isEnd, endDate };
};

// Lille tids-præfiks til en deadline-chip på en bestemt dag
const chipTimePrefix = (d, dateStr) => {
  const { isMultiDay, isStart, isEnd } = getSpanInfo(d, dateStr);
  if (isMultiDay) {
    if (isStart && d.time) return d.time;
    if (isEnd && d.end_time) return `→${d.end_time}`;
    return '';
  }
  if (d.time && d.end_time) return `${d.time}–${d.end_time}`;
  return d.time || '';
};

// Beregn varighed som læsbar dansk streng (fx "2 dage 3 timer", "45 min")
const formatDuration = (d) => {
  const endDate = deadlineEndDate(d);
  const hasEnd = (d.end_date && d.end_date !== d.date) || d.end_time;
  if (!hasEnd) return null;
  const start = new Date(`${d.date}T${d.time || '00:00'}:00`);
  const end = new Date(`${endDate}T${d.end_time || d.time || '00:00'}:00`);
  let mins = Math.round((end - start) / 60000);
  if (mins <= 0) return null;
  const days = Math.floor(mins / 1440); mins -= days * 1440;
  const hours = Math.floor(mins / 60); mins -= hours * 60;
  const parts = [];
  if (days) parts.push(`${days} ${days === 1 ? 'dag' : 'dage'}`);
  if (hours) parts.push(`${hours} ${hours === 1 ? 'time' : 'timer'}`);
  if (mins) parts.push(`${mins} min`);
  return parts.join(' ') || null;
};

// En deadline der strækker sig over flere dage (bjælke i stedet for chip)
const isMultiDayDeadline = (d) => deadlineEndDate(d) !== d.date;

// Beregn sammenhængende bjælker for én uge.
// dateStrs = array af 7 ISO-datostrenge (eller null for tomme celler).
// Returnerer bjælker med kolonne-spænd + lane (række) så de ikke overlapper.
const computeSpanBars = (dateStrs, deadlines) => {
  const raw = [];
  deadlines.filter(isMultiDayDeadline).forEach((d) => {
    const cols = [];
    dateStrs.forEach((ds, i) => { if (ds && deadlineCoversDate(d, ds)) cols.push(i); });
    if (!cols.length) return;
    const startCol = cols[0];
    const endCol = cols[cols.length - 1];
    raw.push({
      deadline: d,
      startCol,
      endCol,
      roundLeft: dateStrs[startCol] === d.date,          // ægte start ligger i denne uge
      roundRight: dateStrs[endCol] === deadlineEndDate(d), // ægte slut ligger i denne uge
    });
  });
  // Greedy lane-tildeling (interval-scheduling) så bjælker ikke overlapper
  raw.sort((a, b) => a.startCol - b.startCol || (a.deadline.date || '').localeCompare(b.deadline.date || ''));
  const laneEnds = [];
  raw.forEach((bar) => {
    let lane = laneEnds.findIndex((end) => end < bar.startCol);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(bar.endCol); }
    else laneEnds[lane] = bar.endCol;
    bar.lane = lane;
  });
  return { bars: raw, laneCount: laneEnds.length };
};

// Overlay med sammenhængende flerdags-bjælker, lagt oven på en 7-kolonners uge.
const SpanBarsOverlay = ({ bars, getStyle, onSelect, top, left, barH, gap }) => (
  <div className="absolute pointer-events-none" style={{ top, left, right: 0, bottom: 0 }}>
    <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: `${barH + gap}px`, columnGap: '2px' }}>
      {bars.map((bar) => {
        const d = bar.deadline;
        const st = getStyle(d);
        return (
          <div
            key={d.id}
            onClick={(e) => { e.stopPropagation(); onSelect(d); }}
            className={`pointer-events-auto truncate border-l-2 cursor-pointer hover:opacity-90 px-1 font-medium ${bar.roundLeft ? 'rounded-l ml-0.5' : ''} ${bar.roundRight ? 'rounded-r mr-0.5' : ''}`}
            style={{
              gridColumn: `${bar.startCol + 1} / ${bar.endCol + 2}`,
              gridRow: bar.lane + 1,
              height: barH,
              lineHeight: `${barH}px`,
              fontSize: barH >= 18 ? '12px' : '10px',
              backgroundColor: st.backgroundColor,
              color: st.color,
              borderLeftColor: st.borderColor,
            }}
            title={`${d.title} (flerdags — klik for detaljer)`}
            data-testid={`deadline-bar-${d.id}`}
          >
            {bar.roundLeft && d.time && <span className="font-mono mr-1">{d.time}</span>}
            {!bar.roundLeft && <span className="mr-0.5">↳</span>}
            {d.title}
            {bar.roundRight && d.end_time && <span className="font-mono ml-1">→{d.end_time}</span>}
          </div>
        );
      })}
    </div>
  </div>
);

export default function Kalender({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [materielItems, setMaterielItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDeadline, setShowAddDeadline] = useState(() => Boolean(location.state?.openAddDeadline));
  const [editingDeadlineId, setEditingDeadlineId] = useState(null); // hvis sat → vi redigerer i stedet for at oprette
  const [newDeadline, setNewDeadline] = useState({ title: '', date: '', time: '', end_date: '', end_time: '', type: '', description: '', custom_name: '', custom_color: '#a855f7', notify_role_ids: [] });
  const [teams, setTeams] = useState([]);
  const [viewMode, setViewMode] = useState('month'); // 'month' eller 'timeline'
  
  const [viewingDeadline, setViewingDeadline] = useState(null); // åbnes ved klik for at vise detaljer
  
  // Deadline-typer (9 globale + brugerdefineret slot)
  const [deadlineTypes, setDeadlineTypes] = useState([]);
  const [showTypesSettings, setShowTypesSettings] = useState(false);
  const [editingType, setEditingType] = useState(null); // { id, name, color }

  const token = localStorage.getItem("token");
  const eventId = localStorage.getItem("selectedEventId");
  const eventName = localStorage.getItem("selectedEventName");

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  
  const monthNames = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 
                      'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
  const dayNames = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

  useEffect(() => {
    loadData();
    loadDeadlineTypes();
  }, [eventId]);

  // Bemærk: hvis man kom fra notifikations-dropdownen med openAddDeadline,
  // åbnes "Tilføj deadline"-modalen via useState-initializeren ovenfor.

  const loadDeadlineTypes = async () => {
    try {
      const res = await axios.get(`${API}/deadline-types`, { headers: { Authorization: `Bearer ${token}` } });
      setDeadlineTypes(res.data || []);
    } catch (e) {
      console.error('Kunne ikke hente deadline-typer', e);
    }
  };

  // Returnér style-objekt (hex farver) for en given deadline
  const getDeadlineStyle = (deadline) => {
    let color;
    if (deadline.type === 'custom') {
      color = deadline.custom_color || '#a855f7';
    } else if (MATERIEL_TYPE_COLORS[deadline.type]) {
      color = MATERIEL_TYPE_COLORS[deadline.type];
    } else {
      const t = deadlineTypes.find(dt => dt.id === deadline.type);
      color = t?.color || '#a855f7';
    }
    return {
      backgroundColor: hexToRgba(color, 0.2),
      borderColor: color,
      color: color,
    };
  };

  // Returnér visningsnavn for en deadline-type (bruges i legende)
  const getDeadlineTypeName = (deadline) => {
    if (deadline.type === 'custom') return deadline.custom_name || 'Brugerdefineret';
    if (deadline.type === 'materiel_bestilling') return 'Bestillingsdeadline';
    if (deadline.type === 'materiel_endelig') return 'Endelig deadline';
    const t = deadlineTypes.find(dt => dt.id === deadline.type);
    return t?.name || deadline.type;
  };

  const loadData = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Hent materiel med deadlines
      const [materielRes, deadlinesRes, rolesRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/materiel`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${eventId}/deadlines`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${eventId}/roles`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setMaterielItems(materielRes.data || []);
      setTeams(rolesRes.data || []);
      
      // Kombiner materiel-deadlines med custom deadlines
      const materielDeadlines = [];
      (materielRes.data || []).forEach(item => {
        if (item.deadline_bestilling) {
          materielDeadlines.push({
            id: `mat-best-${item.id}`,
            title: `Bestillingsdeadline: ${item.produktinformation}`,
            date: item.deadline_bestilling,
            type: 'materiel_bestilling',
            item: item
          });
        }
        if (item.deadline_endelig) {
          materielDeadlines.push({
            id: `mat-end-${item.id}`,
            title: `Endelig deadline: ${item.produktinformation}`,
            date: item.deadline_endelig,
            type: 'materiel_endelig',
            item: item
          });
        }
      });
      
      setDeadlines([...materielDeadlines, ...(deadlinesRes.data || [])]);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
    setLoading(false);
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const prevWeek = () => setCurrentDate(new Date(year, month, currentDate.getDate() - 7));
  const nextWeek = () => setCurrentDate(new Date(year, month, currentDate.getDate() + 7));
  const goToToday = () => setCurrentDate(new Date());

  // Sortér deadlines kronologisk efter dato + klokkeslæt (manglende tid = starten af dagen)
  const deadlineSortKey = (d) => `${d.date || ''}T${d.time || '00:00'}`;
  const sortByDateTime = (a, b) => deadlineSortKey(a).localeCompare(deadlineSortKey(b));

  const getDeadlinesForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return [...deadlines.filter(d => deadlineCoversDate(d, dateStr))].sort(sortByDateTime);
  };

  // Hent deadlines for en specifik ISO-datostreng (yyyy-mm-dd)
  const getDeadlinesForDateStr = (dateStr) => [...deadlines.filter(d => deadlineCoversDate(d, dateStr))].sort(sortByDateTime);

  // Beregn de 7 dage i den uge der indeholder currentDate (mandag-søndag)
  const weekStart = getMondayOfWeek(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i)
  );
  const weekNumber = getISOWeek(currentDate);

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const isUpcoming = (dateStr) => {
    const today = todayMidnight();
    const deadline = new Date(dateStr);
    const diffDays = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  const isPast = (dateStr) => new Date(dateStr) < todayMidnight();

  // Upcoming deadlines (næste 14 dage)
  const upcomingDeadlines = [...deadlines
    .filter(d => {
      const deadline = new Date(d.date);
      const today = todayMidnight();
      const diffDays = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 14;
    })]
    .sort(sortByDateTime);

  const saveDeadline = async () => {
    if (!newDeadline.title || !newDeadline.date || !newDeadline.type) {
      toast.error('Udfyld titel, dato og type');
      return;
    }
    if (newDeadline.type === 'custom' && !newDeadline.custom_name?.trim()) {
      toast.error('Skriv et navn til den brugerdefinerede type');
      return;
    }
    // Validér spænd: slut må ikke ligge før start
    const endDate = newDeadline.end_date || newDeadline.date;
    if (endDate < newDeadline.date) {
      toast.error('Slutdato kan ikke ligge før startdato');
      return;
    }
    if (endDate === newDeadline.date && newDeadline.end_time && newDeadline.time && newDeadline.end_time < newDeadline.time) {
      toast.error('Sluttid kan ikke ligge før starttid på samme dag');
      return;
    }
    try {
      const payload = {
        ...newDeadline,
        time: newDeadline.time || null,
        end_date: newDeadline.end_date || null,
        end_time: newDeadline.end_time || null,
        custom_color: newDeadline.type === 'custom' ? newDeadline.custom_color : null,
        custom_name: newDeadline.type === 'custom' ? newDeadline.custom_name.trim() : null,
      };
      if (editingDeadlineId) {
        await axios.put(`${API}/events/${eventId}/deadlines/${editingDeadlineId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Deadline opdateret');
      } else {
        await axios.post(`${API}/events/${eventId}/deadlines`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Deadline oprettet');
      }
      closeDeadlineModal();
      loadData();
    } catch (error) {
      toast.error(editingDeadlineId ? 'Kunne ikke opdatere deadline' : 'Kunne ikke oprette deadline');
    }
  };

  const closeDeadlineModal = () => {
    setShowAddDeadline(false);
    setEditingDeadlineId(null);
    setNewDeadline({ title: '', date: '', time: '', end_date: '', end_time: '', type: '', description: '', custom_name: '', custom_color: '#a855f7', notify_role_ids: [] });
  };

  // Klik på en deadline → åbn modal i redigeringstilstand
  const openEditDeadline = (deadline) => {
    // Kun deadlines der er auto-genereret fra et materiel-item er låst (de har .item sat).
    // Manuelt oprettede kalender-deadlines kan altid redigeres, uanset type.
    if (deadline.item) {
      toast.info('Materiel-deadlines styres via lager-modulet');
      return;
    }
    setEditingDeadlineId(deadline.id);
    setNewDeadline({
      title: deadline.title || '',
      date: deadline.date || '',
      time: deadline.time || '',
      end_date: deadline.end_date || '',
      end_time: deadline.end_time || '',
      type: deadline.type || '',
      description: deadline.description || '',
      custom_name: deadline.custom_name || '',
      custom_color: deadline.custom_color || '#a855f7',
      notify_role_ids: deadline.notify_role_ids || [],
    });
    setShowAddDeadline(true);
  };

  // Kopiér deadline → åbn ny-deadline-formular med præudfyldt indhold.
  // Dato bevares — brugeren skal selv ændre den til den nye dag.
  const openCopyDeadline = (deadline) => {
    if (deadline.item) {
      toast.info('Materiel-deadlines kan ikke kopieres herfra');
      return;
    }
    setEditingDeadlineId(null);  // VIGTIGT: så det bliver et nyt insert, ikke en update
    setNewDeadline({
      title: deadline.title || '',
      date: deadline.date || '',
      time: deadline.time || '',
      end_date: deadline.end_date || '',
      end_time: deadline.end_time || '',
      type: deadline.type || '',
      description: deadline.description || '',
      custom_name: deadline.custom_name || '',
      custom_color: deadline.custom_color || '#a855f7',
      notify_role_ids: deadline.notify_role_ids || [],
    });
    setShowAddDeadline(true);
    toast.info('Kopi klar — vælg ny dato/tid');
  };

  const deleteCurrentDeadline = async () => {
    if (!editingDeadlineId) return;
    if (!window.confirm('Er du sikker på at du vil slette denne deadline?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}/deadlines/${editingDeadlineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deadline slettet');
      closeDeadlineModal();
      loadData();
    } catch (e) {
      toast.error('Kunne ikke slette deadline');
    }
  };

  // F1: Hurtig sletning direkte fra visnings-dialogen
  const quickDeleteDeadline = async (deadline) => {
    if (!window.confirm(`Slet "${deadline.title}"?`)) return;
    try {
      await axios.delete(`${API}/events/${eventId}/deadlines/${deadline.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deadline slettet');
      setViewingDeadline(null);
      loadData();
    } catch (e) {
      toast.error('Kunne ikke slette deadline');
    }
  };

  const saveTypeEdit = async () => {
    if (!editingType) return;
    try {
      await axios.put(`${API}/deadline-types/${editingType.id}`, {
        name: editingType.name,
        color: editingType.color,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Type opdateret');
      setEditingType(null);
      loadDeadlineTypes();
      loadData(); // genfarv eksisterende deadlines
    } catch (e) {
      toast.error('Kunne ikke opdatere type');
    }
  };

  // Generer kalender-grid (ikke-muterende)
  const calendarDays = [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  // Opdel kalender-grid i uger (rækker à 7 dage) til brug for ugenumre + print
  const calendarWeeks = Array.from(
    { length: Math.ceil(calendarDays.length / 7) },
    (_, i) => calendarDays.slice(i * 7, i * 7 + 7)
  );

  // -------------------- PRINT -----------------------
  // Escape helper til HTML
  const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Render én deadline-celle til print HTML
  const renderPrintDeadline = (d, dateStr) => {
    const st = getDeadlineStyle(d);
    // Flerdags-deadline → sammenhængende bjælke-segment (bløder over cellekanter)
    if (dateStr && isMultiDayDeadline(d)) {
      const { isStart, isEnd } = getSpanInfo(d, dateStr);
      const ml = isStart ? '0' : '-5px';
      const mr = isEnd ? '0' : '-5px';
      const radius = `${isStart ? '2px' : '0'} ${isEnd ? '2px' : '0'} ${isEnd ? '2px' : '0'} ${isStart ? '2px' : '0'}`;
      const time = isStart && d.time ? `<span style="font-family:monospace;margin-right:3px;">${esc(d.time)}</span>` : '';
      const label = isStart ? esc(d.title) : (isEnd && d.end_time ? `→${esc(d.end_time)}` : '&nbsp;');
      const leftBorder = isStart ? `3px solid ${st.borderColor}` : 'none';
      return `<div style="font-size:8pt;padding:2px 4px;margin:0 ${mr} 2px ${ml};border-left:${leftBorder};background:${st.backgroundColor};color:${st.color};border-radius:${radius};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${time}${label}</div>`;
    }
    const time = d.time ? `<span style="font-family:monospace;margin-right:3px;">${esc(d.time)}</span>` : '';
    return `<div style="font-size:8pt;padding:2px 4px;margin-bottom:2px;border-left:3px solid ${st.borderColor};background:${st.backgroundColor};color:${st.color};border-radius:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${time}${esc(d.title)}</div>`;
  };

  // Print-rækkefølge: flerdags-bjælker øverst (så de flugter på tværs af celler), derefter enkeltdags
  const orderForPrint = (dls) => [...dls].sort((a, b) => (isMultiDayDeadline(a) ? 0 : 1) - (isMultiDayDeadline(b) ? 0 : 1));

  const handlePrintCurrentView = () => {
    let bodyHtml = '';
    let subTitle = '';
    const baseStyles = `
      @page { margin: 12mm; }
      body { font-family: -apple-system, system-ui, sans-serif; color: #000; margin: 0; padding: 16px; }
      h1 { font-size: 18pt; margin: 0 0 2pt 0; }
      .sub { font-size: 11pt; color: #666; margin-bottom: 12pt; }
      .empty { padding: 40px; text-align: center; color: #888; }
      table.cal { width: 100%; border-collapse: collapse; table-layout: fixed; }
      table.cal th { text-align: center; font-size: 9pt; text-transform: uppercase; color: #555; padding: 4px; border-bottom: 1px solid #000; }
      table.cal td { border: 1px solid #ccc; vertical-align: top; padding: 4px; height: 90px; width: 14.28%; }
      table.cal td.wk { width: 28px; font-size: 8pt; color: #888; background: #f7f7f7; text-align: center; font-weight: 600; vertical-align: middle; }
      .day-num { font-size: 10pt; font-weight: 600; margin-bottom: 4px; }
      .day-today .day-num { color: #16a34a; }
      table.tl { width: 100%; border-collapse: collapse; }
      table.tl th { text-align: left; font-size: 10pt; text-transform: uppercase; color: #555; padding: 6px 8px; border-bottom: 2px solid #000; }
      table.tl td { padding: 6px 8px; border-bottom: 1px solid #ddd; vertical-align: top; }
      @media print { .no-print { display: none !important; } }
    `;

    if (viewMode === 'month') {
      subTitle = `Kalender — ${monthNames[month]} ${year}`;
      const todayD = new Date();
      const todayStr = `${todayD.getFullYear()}-${String(todayD.getMonth() + 1).padStart(2, '0')}-${String(todayD.getDate()).padStart(2, '0')}`;
      const headerCells = `<th class="wk">Uge</th>` + dayNames.map(n => `<th>${esc(n)}</th>`).join('');
      const rowsHtml = calendarWeeks.map(weekArr => {
        // ugenummer udregnes ud fra første rigtige dag i ugen
        const firstDayInWeek = weekArr.find(x => x);
        const weekDate = firstDayInWeek ? new Date(year, month, firstDayInWeek) : null;
        const wkNum = weekDate ? getISOWeek(weekDate) : '';
        const cellsHtml = weekArr.map(day => {
          if (!day) return `<td style="background:#fafafa;"></td>`;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dls = orderForPrint(getDeadlinesForDateStr(dateStr));
          const isTodayCell = dateStr === todayStr;
          return `<td class="${isTodayCell ? 'day-today' : ''}"><div class="day-num">${day}</div>${dls.map(d => renderPrintDeadline(d, dateStr)).join('')}</td>`;
        }).join('');
        return `<tr><td class="wk">${wkNum}</td>${cellsHtml}</tr>`;
      }).join('');
      bodyHtml = `<table class="cal"><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table>`;
    } else if (viewMode === 'week') {
      const wkStartLabel = weekDays[0].toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
      const wkEndLabel = weekDays[6].toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
      subTitle = `Uge ${weekNumber} — ${wkStartLabel} til ${wkEndLabel}`;
      const todayD = new Date();
      const todayStr = `${todayD.getFullYear()}-${String(todayD.getMonth() + 1).padStart(2, '0')}-${String(todayD.getDate()).padStart(2, '0')}`;
      const dayHeaderShort = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];
      const headerCells = dayHeaderShort.map((n, i) => {
        const dd = weekDays[i];
        return `<th><div>${esc(n)}</div><div style="font-weight:400;color:#666;font-size:8pt;">${dd.getDate()}/${dd.getMonth() + 1}</div></th>`;
      }).join('');
      const cells = weekDays.map(dd => {
        const dateStr = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`;
        const dls = orderForPrint(getDeadlinesForDateStr(dateStr));
        const isTodayCell = dateStr === todayStr;
        return `<td class="${isTodayCell ? 'day-today' : ''}" style="height:240px;">${dls.map(d => renderPrintDeadline(d, dateStr)).join('')}</td>`;
      }).join('');
      bodyHtml = `<table class="cal"><thead><tr>${headerCells}</tr></thead><tbody><tr>${cells}</tr></tbody></table>`;
    } else {
      subTitle = 'Tidslinje — alle deadlines';
      const sorted = [...deadlines].sort(sortByDateTime);
      if (sorted.length === 0) {
        bodyHtml = '<div class="empty">Ingen deadlines at vise</div>';
      } else {
        const rowsHtml = sorted.map(d => {
          const st = getDeadlineStyle(d);
          const typeName = getDeadlineTypeName(d);
          const dateLabel = new Date(d.date).toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
          const timeLabel = d.time ? `<span style="font-family:monospace;margin-left:8px;">${esc(d.time)}</span>` : '';
          const desc = d.description ? `<div style="font-size:10pt;color:#555;margin-top:3px;">${esc(d.description)}</div>` : '';
          return `<tr>
            <td style="white-space:nowrap;width:32%;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${st.borderColor};margin-right:6px;vertical-align:middle;"></span>
              ${esc(dateLabel)}${timeLabel}
            </td>
            <td>
              <div style="font-weight:600;">${esc(d.title)}</div>
              <div style="font-size:9pt;color:#888;">${esc(typeName)}</div>
              ${desc}
            </td>
          </tr>`;
        }).join('');
        bodyHtml = `<table class="tl"><thead><tr><th>Dato</th><th>Deadline</th></tr></thead><tbody>${rowsHtml}</tbody></table>`;
      }
    }

    const html = `<!doctype html><html lang="da"><head><meta charset="utf-8"><title>${esc(eventName || 'Resovia')} — ${esc(subTitle)}</title>
      <style>${baseStyles}</style></head><body>
      <h1>${esc(eventName || 'Resovia')}</h1>
      <div class="sub">${esc(subTitle)}</div>
      ${bodyHtml}
      <div class="no-print" style="margin-top:20pt;text-align:center;">
        <button onclick="window.print()" style="padding:8px 16px;font-size:11pt;cursor:pointer;">Print</button>
        <button onclick="window.close()" style="padding:8px 16px;font-size:11pt;cursor:pointer;margin-left:8px;">Luk</button>
      </div>
      <script>setTimeout(function(){ window.print(); }, 300);</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Pop-up blokeret. Tillad pop-ups for at printe.');
      return;
    }
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="calendar-page">
      <header className="border-b border-border bg-card sticky top-0 z-10 no-print">
        <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/")} variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold uppercase tracking-tight">Kalender</h1>
              <p className="text-xs text-muted-foreground">{eventName || 'Planlægning og deadlines'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={viewMode === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('month')}
              data-testid="view-month-btn"
            >
              <CalendarIcon className="h-4 w-4 mr-1" />
              Måned
            </Button>
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}
              data-testid="view-week-btn"
            >
              <CalendarIcon className="h-4 w-4 mr-1" />
              Uge
            </Button>
            <Button
              variant={viewMode === 'timeline' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('timeline')}
              data-testid="view-timeline-btn"
            >
              <Clock className="h-4 w-4 mr-1" />
              Tidslinje
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePrintCurrentView()}
              data-testid="calendar-print-btn"
              title="Udskriv den aktive visning"
            >
              <CalendarIcon className="h-4 w-4 mr-1" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowAddDeadline(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Tilføj deadline
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Print-titel — vises kun ved udskrift */}
        <div className="print-only print-header" aria-hidden="true">
          <h1>{eventName || 'Resovia'}</h1>
          <p>{viewMode === 'month' ? `${monthNames[month]} ${year}` : 'Tidslinje — alle deadlines'}</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 calendar-grid">
          {/* Hovedkalender */}
          <div className="lg:col-span-3 calendar-main">
            {viewMode === 'month' && (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Måned-navigation */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <Button variant="ghost" size="sm" onClick={prevMonth}>
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="text-center">
                    <h2 className="text-lg font-bold">{monthNames[month]} {year}</h2>
                    <Button variant="link" size="sm" onClick={goToToday} className="text-xs">
                      Gå til i dag
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={nextMonth}>
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>

                {/* Dage header (med ugenummer-kolonne foran) */}
                <div className="grid border-b border-border" style={{ gridTemplateColumns: '40px repeat(7, 1fr)' }}>
                  <div className="px-1 py-2 text-center text-xs font-medium text-muted-foreground bg-muted/30">Uge</div>
                  {dayNames.map(day => (
                    <div key={day} className="px-1 py-2 text-left text-sm font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Kalender grid — én række pr. uge */}
                <div>
                  {calendarWeeks.map((weekArr, wIdx) => {
                    const firstDayInWeek = weekArr.find(x => x);
                    const wkDate = firstDayInWeek ? new Date(year, month, firstDayInWeek) : null;
                    const wkNum = wkDate ? getISOWeek(wkDate) : '';
                    // Datostrenge for ugens 7 kolonner (null for tomme celler)
                    const weekDateStrs = weekArr.map(day => day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null);
                    const { bars, laneCount } = computeSpanBars(weekDateStrs, deadlines);
                    const barsAreaH = laneCount * 20; // 16px bjælke + 4px gab
                    return (
                      <div key={wIdx} className="relative">
                        <div className="grid" style={{ gridTemplateColumns: '40px repeat(7, 1fr)' }}>
                          <div className="flex items-center justify-center text-xs font-semibold text-muted-foreground bg-muted/30 border-b border-r border-border/50">
                            {wkNum}
                          </div>
                          {weekArr.map((day, idx) => {
                            const dayAll = day ? getDeadlinesForDate(day) : [];
                            const singleDay = dayAll.filter(d => !isMultiDayDeadline(d));
                            const today = isToday(day);
                            return (
                              <div
                                key={idx}
                                className={`min-h-[100px] p-1 border-b border-r border-border/50 ${
                                  day ? 'hover:bg-accent/5 cursor-pointer' : 'bg-muted/10'
                                } ${today ? 'bg-primary/5' : ''}`}
                                onClick={() => day && setSelectedDate({ day, month, year, deadlines: dayAll })}
                              >
                                {day && (
                                  <>
                                    <div className={`text-sm font-medium mb-1 ${today ? 'text-primary' : ''}`}>
                                      {day}
                                    </div>
                                    {/* Plads til flerdags-bjælker (overlay) */}
                                    {barsAreaH > 0 && <div style={{ height: barsAreaH }} aria-hidden="true" />}
                                    <div className="space-y-0.5">
                                      {singleDay.slice(0, 3).map(deadline => {
                                        const st = getDeadlineStyle(deadline);
                                        const cellDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                        const tp = chipTimePrefix(deadline, cellDateStr);
                                        return (
                                          <div
                                            key={deadline.id}
                                            onClick={(e) => { e.stopPropagation(); setViewingDeadline(deadline); }}
                                            className="text-[10px] px-1 py-0.5 truncate border-l-2 cursor-pointer hover:opacity-80 rounded"
                                            style={{ backgroundColor: st.backgroundColor, color: st.color, borderLeftColor: st.borderColor }}
                                            title={`${deadline.title} (klik for detaljer)`}
                                            data-testid={`deadline-cell-${deadline.id}`}
                                          >
                                            {tp && <span className="font-mono mr-1">{tp}</span>}
                                            {deadline.title}
                                          </div>
                                        );
                                      })}
                                      {singleDay.length > 3 && (
                                        <div className="text-[10px] text-muted-foreground">
                                          +{singleDay.length - 3} mere
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {/* Sammenhængende flerdags-bjælker oven på ugen */}
                        {bars.length > 0 && (
                          <SpanBarsOverlay
                            bars={bars}
                            getStyle={getDeadlineStyle}
                            onSelect={setViewingDeadline}
                            top={28}
                            left={40}
                            barH={16}
                            gap={4}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {viewMode === 'week' && (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Uge-navigation */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <Button variant="ghost" size="sm" onClick={prevWeek} data-testid="week-prev-btn">
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="text-center">
                    <h2 className="text-lg font-bold">
                      Uge {weekNumber} · {weekDays[0].toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })} – {weekDays[6].toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </h2>
                    <Button variant="link" size="sm" onClick={goToToday} className="text-xs">
                      Gå til denne uge
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={nextWeek} data-testid="week-next-btn">
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>

                {/* Dage header */}
                <div className="grid grid-cols-7 border-b border-border">
                  {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map((dn, i) => {
                    const dd = weekDays[i];
                    const dTodayCheck = new Date();
                    const isHeaderToday = dd.toDateString() === dTodayCheck.toDateString();
                    return (
                      <div key={dn} className={`p-2 text-center text-sm font-medium ${isHeaderToday ? 'text-primary' : 'text-muted-foreground'}`}>
                        <div>{dn}</div>
                        <div className="text-xs font-normal">{dd.getDate()}/{dd.getMonth() + 1}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Dag-celler — store, så hele dagens deadlines kan ses */}
                {(() => {
                  const weekDateStrs = weekDays.map(dd => `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`);
                  const { bars, laneCount } = computeSpanBars(weekDateStrs, deadlines);
                  const barsAreaH = laneCount * 22; // 18px bjælke + 4px gab
                  return (
                    <div className="relative">
                      <div className="grid grid-cols-7" style={{ minHeight: '320px' }}>
                        {weekDays.map((dd, idx) => {
                          const dateStr = weekDateStrs[idx];
                          const dayAll = getDeadlinesForDateStr(dateStr);
                          const singleDay = dayAll.filter(d => !isMultiDayDeadline(d));
                          const dTodayCheck = new Date();
                          const cellToday = dd.toDateString() === dTodayCheck.toDateString();
                          return (
                            <div
                              key={idx}
                              className={`p-2 border-r border-border/50 ${cellToday ? 'bg-primary/5' : 'hover:bg-accent/5'}`}
                              data-testid={`week-cell-${dateStr}`}
                            >
                              {barsAreaH > 0 && <div style={{ height: barsAreaH }} aria-hidden="true" />}
                              <div className="space-y-1">
                                {dayAll.length === 0 && (
                                  <div className="text-[10px] text-muted-foreground/50 italic">—</div>
                                )}
                                {singleDay.map(deadline => {
                                  const st = getDeadlineStyle(deadline);
                                  const tp = chipTimePrefix(deadline, dateStr);
                                  return (
                                    <div
                                      key={deadline.id}
                                      onClick={() => setViewingDeadline(deadline)}
                                      className="text-xs px-1.5 py-1 rounded border-l-2 cursor-pointer hover:opacity-80"
                                      style={{ backgroundColor: st.backgroundColor, color: st.color, borderLeftColor: st.borderColor }}
                                      title={`${deadline.title} (klik for detaljer)`}
                                    >
                                      {tp && <div className="font-mono text-[10px] opacity-80">{tp}</div>}
                                      <div className="font-medium leading-snug">{deadline.title}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {bars.length > 0 && (
                        <SpanBarsOverlay
                          bars={bars}
                          getStyle={getDeadlineStyle}
                          onSelect={setViewingDeadline}
                          top={8}
                          left={0}
                          barH={18}
                          gap={4}
                        />
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {viewMode === 'timeline' && (
              /* Tidslinje visning */
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-lg font-bold mb-4">Tidslinje - Kommende deadlines</h2>
                {deadlines.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">Ingen deadlines endnu</p>
                ) : (
                  <div className="relative">
                    {/* Tidslinje-linje */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                    
                    <div className="space-y-4">
                      {[...deadlines]
                        .sort(sortByDateTime)
                        .map(deadline => {
                          const st = getDeadlineStyle(deadline);
                          const past = isPast(deadline.date);
                          const upcoming = isUpcoming(deadline.date);
                          
                          return (
                            <div key={deadline.id} className="relative pl-10">
                              {/* Tidslinje-punkt */}
                              <div
                                className="absolute left-2 w-5 h-5 rounded-full border-2"
                                style={{ borderColor: st.borderColor, backgroundColor: st.backgroundColor, opacity: past ? 0.5 : 1 }}
                              >
                                {past && <Check className="h-3 w-3 m-0.5 text-green-500" />}
                                {upcoming && !past && <AlertTriangle className="h-3 w-3 m-0.5 text-orange-500" />}
                              </div>

                              <div
                                onClick={() => setViewingDeadline(deadline)}
                                className="p-3 rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                                style={{ borderColor: st.borderColor, backgroundColor: st.backgroundColor, opacity: past ? 0.5 : 1 }}
                                title="Klik for detaljer"
                                data-testid={`deadline-timeline-${deadline.id}`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium" style={{ color: st.color }}>
                                    {new Date(deadline.date).toLocaleDateString('da-DK', { 
                                      weekday: 'short', day: 'numeric', month: 'short' 
                                    })}
                                    {deadline.time && <span className="ml-2 font-mono">{deadline.time}</span>}
                                    {(deadline.end_date && deadline.end_date !== deadline.date) && (
                                      <span className="ml-1">→ {new Date(deadline.end_date).toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                                    )}
                                    {deadline.end_time && (
                                      <span className="ml-1 font-mono">{(deadline.end_date && deadline.end_date !== deadline.date) ? '' : '→ '}{deadline.end_time}</span>
                                    )}
                                  </span>
                                  {upcoming && !past && (
                                    <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">
                                      Snart!
                                    </span>
                                  )}
                                </div>
                                <h3 className="font-medium">{deadline.title}</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {getDeadlineTypeName(deadline)}
                                  {formatDuration(deadline) && <span className="ml-1">· varighed {formatDuration(deadline)}</span>}
                                </p>
                                {deadline.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{deadline.description}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar med upcoming deadlines */}
          <div className="space-y-4 no-print">
            {/* Kommende deadlines alert */}
            {upcomingDeadlines.length > 0 && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="h-5 w-5 text-orange-500" />
                  <h3 className="font-bold text-orange-400">Kommende deadlines</h3>
                </div>
                <div className="space-y-2">
                  {upcomingDeadlines.slice(0, 5).map(deadline => {
                    const daysLeft = Math.ceil((new Date(deadline.date) - new Date()) / (1000 * 60 * 60 * 24));
                    const st = getDeadlineStyle(deadline);
                    
                    return (
                      <div
                        key={deadline.id}
                        onClick={() => setViewingDeadline(deadline)}
                        className="p-2 rounded border-l-2 cursor-pointer hover:opacity-80"
                        style={{ backgroundColor: st.backgroundColor, borderLeftColor: st.borderColor }}
                        title="Klik for detaljer"
                        data-testid={`deadline-upcoming-${deadline.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {daysLeft === 0 ? 'I dag!' : daysLeft === 1 ? 'I morgen' : `${daysLeft} dage`}
                            {deadline.time && <span className="ml-2 font-mono">{deadline.time}</span>}
                          </span>
                          {deadline.type?.includes('materiel') && (
                            <Package className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        <p className="text-sm font-medium truncate">{deadline.title}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Legende */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold">Farveforklaring</h3>
                {(user?.role === 'admin' || user?.role === 'admin_assistant') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTypesSettings(true)}
                    className="h-7 px-2"
                    data-testid="open-type-settings-btn"
                    title="Rediger navn og farve på de globale typer"
                  >
                    <Settings className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {/* Materiel-deadlines er hardcodede */}
                {Object.entries(MATERIEL_TYPE_COLORS).map(([type, color]) => (
                  <div key={type} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded border"
                      style={{ backgroundColor: hexToRgba(color, 0.4), borderColor: color }}
                    />
                    <span className="text-sm">
                      {type === 'materiel_bestilling' && 'Bestillingsdeadline'}
                      {type === 'materiel_endelig' && 'Endelig deadline'}
                    </span>
                  </div>
                ))}
                {/* De 9 brugerredigerbare typer */}
                {deadlineTypes.map((t) => (
                  <div key={t.id} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded border"
                      style={{ backgroundColor: hexToRgba(t.color, 0.4), borderColor: t.color }}
                    />
                    <span className="text-sm">{t.name}</span>
                  </div>
                ))}
                {/* Brugerdef-slot — vises blot som "Brugerdefineret (vælges pr. deadline)" */}
                <div className="flex items-center gap-2 pt-1 border-t border-border/30 mt-2">
                  <div className="w-3 h-3 rounded border border-dashed border-muted-foreground" />
                  <span className="text-sm text-muted-foreground italic">Brugerdefineret (vælges pr. deadline)</span>
                </div>
              </div>
            </div>

            {/* Valgt dato detaljer */}
            {selectedDate && selectedDate.deadlines.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-bold mb-3">
                  {selectedDate.day}. {monthNames[selectedDate.month]} {selectedDate.year}
                </h3>
                <div className="space-y-2">
                  {selectedDate.deadlines.map(deadline => {
                    const st = getDeadlineStyle(deadline);
                    return (
                      <div
                        key={deadline.id}
                        className="p-2 rounded border"
                        style={{ backgroundColor: st.backgroundColor, borderColor: st.borderColor }}
                      >
                        <p className="font-medium text-sm">{deadline.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{getDeadlineTypeName(deadline)}{deadline.time && ` · ${deadline.time}`}</p>
                        {deadline.item && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Leverandør: {deadline.item.leverandoer || '-'}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Vis deadline-detaljer (klik på deadline → forstør) */}
      {viewingDeadline && (() => {
        const d = viewingDeadline;
        const st = getDeadlineStyle(d);
        // Kun deadlines med .item (auto-genereret fra materiel) er låst
        const canEdit = !d.item;
        const dateLabel = (() => {
          try {
            return new Date(d.date).toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
          } catch { return d.date; }
        })();
        return (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 no-print" onClick={() => setViewingDeadline(null)}>
            <div className="bg-card border border-border rounded-t-xl sm:rounded-lg w-full sm:max-w-md max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* Farve-banner i toppen */}
              <div className="h-2 rounded-t-xl sm:rounded-t-lg" style={{ backgroundColor: st.borderColor }} />
              <div className="p-6">
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide mb-1" style={{ color: st.color }}>
                      {getDeadlineTypeName(d)}
                    </div>
                    <h2 className="text-xl font-bold break-words" data-testid="deadline-view-title">{d.title}</h2>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setViewingDeadline(null)} data-testid="deadline-view-close-btn">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="capitalize">{dateLabel}{d.time && <span className="ml-1 font-mono">{d.time}</span>}</span>
                  </div>
                  {((d.end_date && d.end_date !== d.date) || d.end_time) && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>
                        Slutter:{' '}
                        <span className="capitalize">
                          {(d.end_date && d.end_date !== d.date)
                            ? new Date(d.end_date).toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                            : dateLabel}
                        </span>
                        {d.end_time && <span className="ml-1 font-mono">{d.end_time}</span>}
                      </span>
                    </div>
                  )}
                  {formatDuration(d) && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>Varighed: {formatDuration(d)}</span>
                    </div>
                  )}
                  {d.description && (
                    <div className="pt-2 border-t border-border/40">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Beskrivelse</p>
                      <p className="whitespace-pre-wrap break-words">{d.description}</p>
                    </div>
                  )}
                  {d.item && (
                    <div className="pt-2 border-t border-border/40">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Materiel</p>
                      <p>Leverandør: <span className="font-medium">{d.item.leverandoer || '-'}</span></p>
                    </div>
                  )}
                  {!canEdit && (
                    <div className="pt-2 border-t border-border/40">
                      <p className="text-xs text-muted-foreground italic">
                        Denne deadline er knyttet til et materiel-item i lager-modulet. Datoen styres derfra.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <Button variant="outline" onClick={() => setViewingDeadline(null)}>Luk</Button>
                  {canEdit ? (
                    <>
                      <Button
                        variant="destructive"
                        onClick={() => quickDeleteDeadline(d)}
                        data-testid="deadline-view-delete-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Slet
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => { const cur = d; setViewingDeadline(null); openCopyDeadline(cur); }}
                        data-testid="deadline-view-copy-btn"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Kopier
                      </Button>
                      <Button
                        onClick={() => { const cur = d; setViewingDeadline(null); openEditDeadline(cur); }}
                        data-testid="deadline-view-edit-btn"
                      >
                        Rediger
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => { setViewingDeadline(null); navigate('/lager'); }}
                      data-testid="deadline-view-goto-lager-btn"
                    >
                      Rediger i lager
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tilføj/Rediger deadline modal */}
      {showAddDeadline && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 no-print">
          <div className="bg-card border border-border rounded-t-xl sm:rounded-lg p-6 w-full sm:max-w-md max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editingDeadlineId ? 'Rediger deadline' : 'Tilføj deadline'}</h2>
              <Button variant="ghost" size="sm" onClick={closeDeadlineModal}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Titel</label>
                <Input 
                  value={newDeadline.title} 
                  onChange={(e) => setNewDeadline({...newDeadline, title: e.target.value})}
                  placeholder="F.eks. Bestilling af generatorer"
                  data-testid="deadline-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Dato</label>
                  <div className="flex gap-1">
                    <Input 
                      type="date"
                      value={newDeadline.date} 
                      onChange={(e) => setNewDeadline({...newDeadline, date: e.target.value})}
                      data-testid="deadline-date-input"
                      className="flex-1"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="shrink-0"
                          aria-label="Vælg dato fra mini-kalender"
                          data-testid="deadline-date-picker-btn"
                        >
                          <CalendarIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <MiniCalendar
                          mode="single"
                          locale={da}
                          weekStartsOn={1}
                          selected={newDeadline.date ? new Date(newDeadline.date + 'T00:00:00') : undefined}
                          onSelect={(d) => {
                            if (!d) return;
                            // Format YYYY-MM-DD lokal tid (undgå UTC-skift)
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            setNewDeadline({ ...newDeadline, date: `${y}-${m}-${day}` });
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Klokkeslæt <span className="text-muted-foreground">(valgfri)</span></label>
                  <Input 
                    type="time"
                    value={newDeadline.time} 
                    onChange={(e) => setNewDeadline({...newDeadline, time: e.target.value})}
                    data-testid="deadline-time-input"
                  />
                </div>
              </div>

              {/* Spændende deadline: valgfri slutdato + sluttid (kan strække sig over minutter/timer/dage) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Slutdato <span className="text-muted-foreground">(valgfri)</span></label>
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      value={newDeadline.end_date}
                      min={newDeadline.date || undefined}
                      onChange={(e) => setNewDeadline({...newDeadline, end_date: e.target.value})}
                      data-testid="deadline-end-date-input"
                      className="flex-1"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="shrink-0"
                          aria-label="Vælg slutdato fra mini-kalender"
                          data-testid="deadline-end-date-picker-btn"
                        >
                          <CalendarIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <MiniCalendar
                          mode="single"
                          locale={da}
                          weekStartsOn={1}
                          selected={newDeadline.end_date ? new Date(newDeadline.end_date + 'T00:00:00') : undefined}
                          onSelect={(d) => {
                            if (!d) return;
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            setNewDeadline({ ...newDeadline, end_date: `${y}-${m}-${day}` });
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Sluttid <span className="text-muted-foreground">(valgfri)</span></label>
                  <Input
                    type="time"
                    value={newDeadline.end_time}
                    onChange={(e) => setNewDeadline({...newDeadline, end_time: e.target.value})}
                    data-testid="deadline-end-time-input"
                  />
                </div>
              </div>
              {((newDeadline.end_date && newDeadline.end_date !== newDeadline.date) || newDeadline.end_time) && formatDuration({ date: newDeadline.date, time: newDeadline.time, end_date: newDeadline.end_date, end_time: newDeadline.end_time }) && (
                <p className="text-xs text-muted-foreground -mt-1" data-testid="deadline-duration-hint">
                  Varighed: {formatDuration({ date: newDeadline.date, time: newDeadline.time, end_date: newDeadline.end_date, end_time: newDeadline.end_time })}
                </p>
              )}


              <div>
                <label className="text-sm font-medium mb-1 block">Type</label>
                {/* 9 brugerredigerbare typer */}
                <div className="grid grid-cols-3 gap-1.5">
                  {deadlineTypes.map(t => {
                    const isSel = newDeadline.type === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setNewDeadline({ ...newDeadline, type: t.id })}
                        className={`text-xs px-2 py-1.5 rounded border text-left truncate ${isSel ? 'ring-2 ring-primary' : ''}`}
                        style={{ backgroundColor: hexToRgba(t.color, isSel ? 0.4 : 0.15), borderColor: t.color, color: t.color }}
                        title={t.name}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
                {/* Brugerdefineret slot */}
                <button
                  type="button"
                  onClick={() => setNewDeadline({ ...newDeadline, type: 'custom' })}
                  className={`mt-1.5 w-full text-xs px-2 py-1.5 rounded border border-dashed text-left ${newDeadline.type === 'custom' ? 'ring-2 ring-primary' : ''}`}
                  data-testid="deadline-type-custom-btn"
                >
                  + Brugerdefineret (vælg eget navn & farve)
                </button>
              </div>

              {/* Felter for brugerdefineret type */}
              {newDeadline.type === 'custom' && (
                <div className="space-y-2 p-3 rounded-md border border-dashed border-primary/40 bg-primary/5">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Navn på denne type</label>
                    <Input
                      value={newDeadline.custom_name}
                      onChange={(e) => setNewDeadline({ ...newDeadline, custom_name: e.target.value })}
                      placeholder="F.eks. Levering af containere"
                      data-testid="deadline-custom-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Farve</label>
                    <div className="flex flex-wrap gap-1.5">
                      {COLOR_PALETTE.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewDeadline({ ...newDeadline, custom_color: color })}
                          className={`w-7 h-7 rounded-full border-2 ${newDeadline.custom_color === color ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                          style={{ backgroundColor: color, borderColor: color }}
                          title={color}
                          data-testid={`deadline-custom-color-${color}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium mb-1 block">Beskrivelse (valgfri)</label>
                <Input 
                  value={newDeadline.description} 
                  onChange={(e) => setNewDeadline({...newDeadline, description: e.target.value})}
                  placeholder="Yderligere detaljer..."
                />
              </div>

              {/* E2: Push-notifikation til valgte teams når opgaven starter */}
              {teams.length > 0 && (
                <div className="pt-2 border-t border-border/40">
                  <label className="text-sm font-medium mb-1 flex items-center gap-1.5">
                    <Bell className="h-3.5 w-3.5" />
                    Send notifikation til teams (valgfri)
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    De valgte teams får en push-notifikation, når opgaven starter.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {teams.map(team => {
                      const selected = (newDeadline.notify_role_ids || []).includes(team.id);
                      return (
                        <button
                          key={team.id}
                          type="button"
                          onClick={() => setNewDeadline(prev => ({
                            ...prev,
                            notify_role_ids: selected
                              ? (prev.notify_role_ids || []).filter(id => id !== team.id)
                              : [...(prev.notify_role_ids || []), team.id]
                          }))}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            selected
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-secondary/50 text-muted-foreground border-border hover:bg-secondary'
                          }`}
                          data-testid={`deadline-notify-team-${team.id}`}
                        >
                          {selected && <Check className="h-3 w-3 inline mr-1" />}
                          {team.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center gap-2 pt-2">
                {editingDeadlineId ? (
                  <Button
                    variant="outline"
                    onClick={deleteCurrentDeadline}
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10 border-red-500/30"
                    data-testid="deadline-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Slet
                  </Button>
                ) : <div />}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={closeDeadlineModal}>Annuller</Button>
                  <Button onClick={saveDeadline} data-testid="deadline-save-btn">
                    {editingDeadlineId ? 'Gem ændringer' : 'Gem deadline'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rediger globale deadline-typer */}
      {showTypesSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-card border border-border rounded-t-xl sm:rounded-lg p-6 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold">Rediger deadline-typer</h2>
                <p className="text-xs text-muted-foreground">Disse typer er globale på tværs af alle festivaler.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowTypesSettings(false); setEditingType(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {deadlineTypes.map(t => (
                <div key={t.id} className="p-2 rounded border border-border">
                  {editingType?.id === t.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editingType.name}
                        onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                        maxLength={60}
                        data-testid={`type-edit-name-${t.id}`}
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {COLOR_PALETTE.map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditingType({ ...editingType, color })}
                            className={`w-7 h-7 rounded-full border-2 ${editingType.color === color ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                            style={{ backgroundColor: color, borderColor: color }}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => setEditingType(null)}>Annuller</Button>
                        <Button size="sm" onClick={saveTypeEdit}>Gem</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded border"
                          style={{ backgroundColor: hexToRgba(t.color, 0.4), borderColor: t.color }}
                        />
                        <span className="text-sm">{t.name}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setEditingType({ ...t })} className="h-7">
                        Rediger
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Print-styling: åbnes via browserens print-dialog. Sider justeres automatisk
          til papirformat — admin vælger selv A4/A3/PDF i print-dialogen. */}
      <style>{`
        .print-only { display: none; }
        @media print {
          @page { margin: 12mm; }
          body, html { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .print-header h1 {
            font-size: 18pt; font-weight: 800; margin: 0 0 2mm 0;
            color: #000;
          }
          .print-header p {
            font-size: 11pt; color: #444; margin: 0 0 6mm 0;
          }
          /* Fold sidebar væk og lad kalender fylde hele bredden */
          .calendar-grid { display: block !important; }
          .calendar-main { width: 100% !important; max-width: 100% !important; }
          /* Lys baggrund og mørk tekst — fungerer godt på print */
          .bg-card, .bg-background { background: #fff !important; color: #000 !important; }
          .text-muted-foreground { color: #666 !important; }
          .border, .border-border { border-color: #999 !important; }
          /* Behold farveinformation på deadlines */
          [class*="bg-"], [style*="background"] {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          /* Skjul navigations-knapper (← →) i månedsoverskriften */
          button { visibility: hidden; }
          button.print-keep, button.print-keep * { visibility: visible !important; }
          /* Sørg for tidslinje-prikker stadig vises */
          .leaflet-image-layer { print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
